import { watch } from 'vue';
import { useLayoutStore } from '@/store/layout';
import { useAlertStore } from '@/store/alertStore';

interface ListingFetchParams {
  page: number;
  itemsPerPage: number;
  sort?: string;
  filters?: string;
}

type FetchFn = (params: ListingFetchParams) => Promise<{ data: any[]; total: number }>;
type MutateFn = (data: any) => Promise<any>;
type Mutate2Fn = (data: any, body: any) => Promise<any>;

export function useModuleLayoutFetcher(
  moduleName: string,
  fetchFn: FetchFn,
  options?: {
    create?: MutateFn;
    update?: MutateFn;
    remove?: MutateFn;
    exportCsv?: Mutate2Fn;
    refresh?: Mutate2Fn;
  }
) {
  const layoutStore = useLayoutStore();
  layoutStore.initLayout(moduleName);

  // Función para extraer los parámetros clave que afectan la paginación
  const getPagingParams = () => {
    const layout = layoutStore.getLayout(moduleName);
    if (!layout) return {};
    const { filters, sortBy, direction, itemsPerPage } = layout;
    return {
      filters: JSON.stringify(filters || {}),
      sort: sortBy ? (direction === 'desc' ? `-${sortBy}` : sortBy) : '',
      itemsPerPage,
    };
  };

  // Watch especial para resetear página cuando filtros, sort o itemsPerPage cambian
  watch(
    () => getPagingParams(),
    (newParams, oldParams) => {
      if (!oldParams) return;
      if (
        newParams.filters !== oldParams.filters ||
        newParams.sort !== oldParams.sort ||
        newParams.itemsPerPage !== oldParams.itemsPerPage
      ) {
        layoutStore.updateLayout(moduleName, { page: 1 });
      }
    },
    { deep: true }
  );

  // Extra: función para obtener todos los params que afectan el fetch
  const getTrackedParams = () => {
    const layout = layoutStore.getLayout(moduleName);
    if (!layout) return null;

    const { page, itemsPerPage, sortBy, direction, filters } = layout;
    const sort = sortBy ? (direction === 'desc' ? `-${sortBy}` : sortBy) : null;

    return JSON.stringify({ page, itemsPerPage, sort, filters });
  };

  let lastParams = getTrackedParams();

  const reload = async () => {
    const layout = layoutStore.getLayout(moduleName);
    if (!layout) return;

    const { page, itemsPerPage, sortBy, direction, filters } = layout;

    const hasFilters = filters && Object.keys(filters).length > 0;

    const flatFilters = hasFilters
      ? Object.entries(filters)
          .filter(([_, v]) => v != null && v !== '')
          .reduce((acc, [k, v]) => {
            acc[`filter[${k}]`] = v;
            return acc;
          }, {} as Record<string, any>)
      : {};

    const params: ListingFetchParams = {
      page,
      itemsPerPage,
      ...(sortBy && { sort: direction === 'desc' ? `-${sortBy}` : sortBy }),
      ...(hasFilters && flatFilters),
    };

    layoutStore.updateLayout(moduleName, { dataLoading: true });

    try {
      const result = await fetchFn(params);
      layoutStore.updateLayout(moduleName, {
        totalItems: result.total,
      });
    } catch (error) {
      console.error(`[${moduleName}] Error fetching data:`, error);
    } finally {
      layoutStore.updateLayout(moduleName, { dataLoading: false });
    }
  };

  // Watch para recargar cuando cambian los params relevantes (incluyendo page)
  watch(
    () => getTrackedParams(),
    async (newParams, oldParams) => {
      if (newParams === oldParams) return;
      await reload();
      lastParams = newParams;
    },
    { immediate: true }
  );

  // Create
  const create = async (data: any) => {
    const alertStore = useAlertStore();
    try {
      if (!options?.create) return;
      const result = await options.create(data);
      alertStore.showAlert(result?.message || 'Registro creado exitosamente.', 'success');
      await reload();
    } catch (err: any) {
      console.error('[Create Error]', err);

      const message = err?.response?.data?.message || 'Ocurrió un error al crear.';
      const errors = err?.response?.data?.errors;

      let fullMessage = message;

      if (errors && typeof errors === 'object') {
        const allMessages = Object.values(errors).flat();
        const uniqueMessages = Array.from(new Set(allMessages));
        const detailedErrors = uniqueMessages.join('\n');
        fullMessage += `\n${detailedErrors}`;
      }

      alertStore.showAlert(fullMessage, 'error');
      throw 'error';
    }
  };

  // Update
  const update = async (data: any) => {
    const alertStore = useAlertStore();
    try {
      if (!options?.update) return;
      const result = await options.update(data);
      alertStore.showAlert(result?.message || 'Registro actualizado exitosamente.', 'success');
      await reload();
    } catch (err: any) {
      console.error('[Update Error]', err);

      const message = err?.response?.data?.message || 'Ocurrió un error al actualizar.';
      const errors = err?.response?.data?.errors;

      let fullMessage = message;

      if (errors && typeof errors === 'object') {
        const allMessages = Object.values(errors).flat();
        const uniqueMessages = Array.from(new Set(allMessages));
        const detailedErrors = uniqueMessages.join('\n');
        fullMessage += `\n${detailedErrors}`;
      }

      alertStore.showAlert(fullMessage, 'error');
    }
  };

  // Delete
  const remove = async (data: any) => {
    const alertStore = useAlertStore();
    try {
      if (!options?.remove) return;
      await options.remove(data);
      alertStore.showAlert('Registro eliminado correctamente.', 'success');
      await reload();
    } catch (err: any) {
      console.error(err);
      alertStore.showAlert(
        err?.response?.data?.message || 'Ocurrió un error al eliminar.',
        'error'
      );
    }
  };
  
  const exportCsv = async (params: any, body:any) => {
    const alertStore = useAlertStore();
    try {
      if (!options?.exportCsv) throw new Error('No hay función de exportCsv definida');
      const result = await options.exportCsv(params, body);
      // Puedes poner aquí un alert si quieres: 
      // alertStore.showAlert('Archivo exportado con éxito', 'success');
      return result;
    } catch (err: any) {
      console.error('[Export CSV Error]', err);

      const message = err?.response?.data?.message || 'Ocurrió un error al exportar CSV.';
      alertStore.showAlert(message, 'error');
      throw err;
    }
  };
  
  const refresh = (overrideParams: Partial<ListingFetchParams> = {}) => {
    // Saca los valores actuales del layout (esto te asegura tener los requeridos)
    const layout = layoutStore.getLayout(moduleName);
    if (!layout) return;
  
    const { page, itemsPerPage, sortBy, direction, filters } = layout;
    const sort = sortBy ? (direction === 'desc' ? `-${sortBy}` : sortBy) : undefined;
    const hasFilters = filters && Object.keys(filters).length > 0;
  
    const flatFilters = hasFilters
      ? Object.entries(filters)
          .filter(([_, v]) => v != null && v !== '')
          .reduce((acc, [k, v]) => {
            acc[`filter[${k}]`] = v;
            return acc;
          }, {} as Record<string, any>)
      : {};
  
    // Crea el objeto final con los defaults y lo que mande el usuario
    const baseParams: ListingFetchParams = {
      page,
      itemsPerPage,
      sort,
      ...flatFilters,
      ...overrideParams,
    };
  
    return fetchFn(baseParams);
  };

  return {
    create,
    update,
    remove,
    reload,
    exportCsv,
    refresh
  };
}
